import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  MailOutline as MailOutlineIcon,
} from '@mui/icons-material';
import { userAPI, teamAPI, organizationAPI } from '../services/api';
import { sendWelcomeEmail } from '../services/emailService';
import { User, Organization, Team } from '../types';
import { useAuth } from '../context/AuthContext';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orgFilter, setOrgFilter] = useState<string>('');
  
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const isBasicManager = isManager && currentUser?.accountType === 'basic';
  // For managers, backend already returns only employees in their org, so this is their people count
  const managerPeopleCount = isManager ? users.length : 0;
  const managerCap = 20;
  const managerHasReachedCap = isBasicManager && managerPeopleCount >= managerCap;
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    name: string;
    email: string;
    password: string;
    role: 'admin' | 'manager' | 'employee' | 'community';
    accountType: 'basic' | 'standard';
    department: string;
    position: string;
    organization: string;
    team: string;
  }>({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    password: '',
    role: 'employee',
    accountType: 'basic',
    department: '',
    position: '',
    organization: '',
    team: '',
  });

  useEffect(() => {
    fetchUsers();
    if (isAdmin) {
      fetchOrganizations();
    }
    // Fetch teams for both admins and managers so we can display team names in the table
    fetchTeams();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getUsers();
      setUsers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvite = async (user: User) => {
    try {
      setError('');
      setSuccess('');
      await userAPI.resendInvite(user._id);
      setSuccess('Invite resent successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend invite');
    }
  };

  // Derive filtered users for admin by organization
  const filteredUsers = React.useMemo(() => {
    if (!isAdmin || !orgFilter) return users;
    return users.filter((u) => {
      const orgId = typeof u.organization === 'object' ? (u.organization as any)?._id : (u.organization as any);
      return orgId === orgFilter;
    });
  }, [users, isAdmin, orgFilter]);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationAPI.getOrganizations();
      setOrganizations(response.data);
    } catch (err: any) {
      console.error('Failed to fetch organizations:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await teamAPI.getTeams();
      setTeams(response.data);
    } catch (err: any) {
      console.error('Failed to fetch teams:', err);
    }
  };

  const handleOpenCreate = () => {
    if (managerHasReachedCap) {
      setError('You have reached the limit of 20 people for a basic manager account. Please contact an administrator to upgrade your account.');
      return;
    }
    setDialogMode('create');
    setFormData({
      firstName: '',
      lastName: '',
      name: '',
      email: '',
      password: '',
      role: 'employee',
      accountType: 'basic',
      department: '',
      position: '',
      organization: '',
      team: '',
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (user: User) => {
    setDialogMode('edit');
    setSelectedUser(user);
    const fullName = user.name || '';
    const parts = fullName.trim().split(/\s+/);
    const firstName = user.firstName || parts[0] || '';
    const lastName = user.lastName || parts.slice(1).join(' ');
    setFormData({
      firstName,
      lastName,
      name: fullName,
      email: user.email,
      password: '',
      role: user.role,
      accountType: user.accountType || 'basic',
      department: user.department || '',
      position: user.position || '',
      organization:
        typeof user.organization === 'object' && user.organization
          ? (user.organization as any)._id
          : (user.organization as any) || '',
      team:
        typeof user.team === 'object' && user.team
          ? (user.team as any)._id
          : (user.team as any) || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    console.log('handleSubmit called, dialogMode:', dialogMode);
    console.log('formData:', formData);

    try {
      if (dialogMode === 'create') {
        console.log('Creating user...');
        const { firstName, lastName, ...rest } = formData;
        const name = `${firstName} ${lastName}`.trim();
        const response = await userAPI.createUser({
          ...rest,
          firstName,
          lastName,
          name,
        });
        
        // Send welcome email if user is an employee and has a welcome token
        console.log('User creation response:', {
          role: response.data.role,
          hasWelcomeToken: !!response.data.welcomeToken,
          email: response.data.email
        });
        
        if (response.data.role === 'employee' && response.data.welcomeToken) {
          console.log('Attempting to send welcome email...');
          try {
            const appBaseUrl = window.location.origin;
            const welcomeUrl = `${appBaseUrl}/welcome?token=${encodeURIComponent(response.data.welcomeToken)}`;
            const greetingName = firstName || name.split(' ')[0] || '';
            
            console.log('Welcome email params:', {
              to_email: response.data.email,
              to_name: greetingName,
              welcome_url: welcomeUrl,
            });
            
            await sendWelcomeEmail({
              to_email: response.data.email,
              to_name: greetingName,
              welcome_url: welcomeUrl,
            });
            
            setSuccess('User created successfully and welcome email sent!');
          } catch (emailError) {
            console.error('Failed to send welcome email:', emailError);
            setSuccess('User created successfully, but welcome email failed to send.');
          }
        } else {
          console.log('Not sending welcome email - either not employee or no welcome token');
          setSuccess('User created successfully!');
        }
      } else if (selectedUser) {
        const { firstName, lastName, ...rest } = formData;
        const name = `${firstName} ${lastName}`.trim();
        const updateData: any = {
          ...rest,
          firstName,
          lastName,
          name,
        };
        // Don't send password if it's empty (not changing it)
        if (!updateData.password) {
          delete updateData.password;
        }
        await userAPI.updateUser(selectedUser._id, updateData);
        setSuccess('User updated successfully!');
      }
      
      handleCloseDialog();
      fetchUsers();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${dialogMode} user`);
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await userAPI.deleteUser(userToDelete._id);
      setSuccess('User deleted successfully!');
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      fetchUsers();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
      setDeleteConfirmOpen(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="h4" component="h1">
            {isAdmin ? 'User Management' : 'Your people'}
          </Typography>
          {isBasicManager && (
            <Typography variant="body2" color={managerHasReachedCap ? 'error' : 'text.secondary'}>
              {`You have added ${managerPeopleCount} of ${managerCap} people.`}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isAdmin && (
            <TextField
              select
              size="small"
              label="Filter by Organization"
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">All Organizations</MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>
              ))}
            </TextField>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            disabled={managerHasReachedCap}
            title={managerHasReachedCap ? 'Basic manager accounts can manage up to 20 people. Please contact an administrator to upgrade your account.' : ''}
          >
            {isAdmin ? 'Add User' : 'Add Person'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              {isAdmin ? (
                <TableCell>Organization</TableCell>
              ) : (
                <>
                  <TableCell>Department</TableCell>
                  <TableCell>Team</TableCell>
                </>
              )}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user._id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={user.role.toUpperCase()} 
                    color={getRoleColor(user.role)} 
                    size="small" 
                  />
                </TableCell>
                {isAdmin ? (
                  <TableCell>
                    {(() => {
                      if (typeof user.organization === 'object' && user.organization) return (user.organization as any).name || '-';
                      if (typeof user.organization === 'string' && user.organization) {
                        const org = organizations.find((o) => o._id === user.organization);
                        return org ? org.name : '-';
                      }
                      return '-';
                    })()}
                  </TableCell>
                ) : (
                  <>
                    <TableCell>{user.department || '-'}</TableCell>
                    <TableCell>
                      {(() => {
                        if (typeof user.team === 'object' && user.team) return (user.team as any).name;
                        if (typeof user.team === 'string' && user.team) {
                          const t = teams.find((tm) => tm._id === user.team);
                          return t ? t.name : '-';
                        }
                        return '-';
                      })()}
                    </TableCell>
                  </>
                )}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEdit(user)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  {(isAdmin || isManager) && user.role === 'employee' && (
                    <IconButton
                      size="small"
                      onClick={() => handleResendInvite(user)}
                      title="Resend invite"
                    >
                      <MailOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                  {(isAdmin || isManager) && user._id !== currentUser?._id && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(user)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' 
            ? (isAdmin ? 'Add New User' : 'Add New Person')
            : (isAdmin ? 'Edit User' : 'Edit Person')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </Box>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <TextField
                fullWidth
                label={dialogMode === 'create' ? 'Password' : 'Password (leave blank to keep current)'}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required={dialogMode === 'create'}
              />
              {isAdmin && (
                <TextField
                  fullWidth
                  select
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="employee">Employee</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="community">Community</MenuItem>
                </TextField>
              )}
              {isAdmin && formData.role === 'manager' && (
                <TextField
                  fullWidth
                  select
                  label="Account Type"
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleChange}
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                </TextField>
              )}
              <TextField
                fullWidth
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleChange}
              />
              {isAdmin && (
                <TextField
                  fullWidth
                  select
                  label="Organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org._id} value={org._id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {isManager && teams.length > 0 && (
                <TextField
                  fullWidth
                  select
                  label="Team"
                  name="team"
                  value={formData.team || ''}
                  onChange={handleChange}
                >
                  <MenuItem value="">No Team</MenuItem>
                  {teams.map((team) => (
                    <MenuItem key={team._id} value={team._id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
